/*
  File: src/auth/AuthContext.js
  Purpose: Provide authentication state and actions across the app via React Context.
  Responsibilities:
  - On mount, check current session via /users/me and hydrate user.
  - Manage idle timeout; auto-logout after inactivity across tabs.
  - Expose login, signup, and logout helpers using the shared apiClient.
  - Persist minimal user info in localStorage for quick access.
  Exposed context shape:
  {
    isAuthenticated: boolean,
    user: object|null,
    login(credentials), signup(credentials), logout(),
    authChecked: boolean // initial auth probe finished
  }
  Notes:
  - Uses navigator.sendBeacon/fetch keepalive to best-effort sync cart on logout.
  - Idle timeout default: 15 minutes; activity is shared via localStorage key.
*/
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from './api';

const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  login: async (_credentials) => { },
  signup: async (_credentials) => { },
  logout: () => { },
  authChecked: false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const IDLE_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
  const LAST_ACTIVITY_KEY = 'auth:lastActivityAt';

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiClient.get('/users/me');
        if (response.data) {
          setUser(response.data);
          try { localStorage.setItem('auth:user', JSON.stringify(response.data)); } catch {}
        }
      } catch (_error) {
        localStorage.removeItem('auth:user');
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  // Idle timeout management and cross-tab/user updates
  useEffect(() => {
    let idleTimer = null;

    const setActivity = () => {
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      resetTimer();
    };

    const resetTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || '0');
        if (Date.now() - last >= IDLE_LIMIT_MS) {
          // expire session
          setUser(null);
          localStorage.removeItem('auth:user');
          apiClient.post('/users/logout').catch(() => { });
        }
      }, IDLE_LIMIT_MS);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'visibilitychange'];
    activityEvents.forEach((evt) => window.addEventListener(evt, setActivity, { passive: true }));

    // initialize on mount
    setActivity();
    resetTimer();

    // cross-tab sync and auth:user hydration
    const onStorage = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        resetTimer();
      }
      if (e.key === 'auth:user') {
        try {
          const next = e.newValue ? JSON.parse(e.newValue) : null;
          setUser(next);
        } catch (_) { }
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, setActivity));
      window.removeEventListener('storage', onStorage);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, []);

  const login = async ({ email, password }) => {
    try {
      const response = await apiClient.post('/users/login', { email, password });
      const { user: userData } = response.data;
      // Cookie is set by server; hydrate full profile to ensure avatar_url is present
      let hydrated = userData;
      try {
        const me = await apiClient.get('/users/me');
        if (me?.data) hydrated = me.data;
      } catch (_e) { }
      localStorage.setItem('auth:user', JSON.stringify(hydrated));
      setUser(hydrated);

      return userData;
    } catch (error) {
      setUser(null);
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signup = async ({ name, email, password, email_opt_in }) => {
    try {
      const response = await apiClient.post('/users/signup', { name, email, password, email_opt_in });
      const { user: userData } = response.data;
      // Cookie is set by server; hydrate full profile to ensure avatar_url is present
      let hydrated = userData;
      try {
        const me = await apiClient.get('/users/me');
        if (me?.data) hydrated = me.data;
      } catch (_e) { }
      localStorage.setItem('auth:user', JSON.stringify(hydrated));
      setUser(hydrated);

      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  };

  const logout = () => {
    // Best-effort: persist cart before logging out using localStorage snapshot
    try {
      const raw = localStorage.getItem('cart:items:v1');
      const items = raw ? JSON.parse(raw) : [];
      const authRaw = localStorage.getItem('auth:user');
      const u = authRaw ? JSON.parse(authRaw) : null;
      if (u && Array.isArray(items) && items.length > 0) {
        const base = apiClient.defaults.baseURL || '';
        const urls = [
          `${base}/carts/sync`,
          base.includes('/dev') ? base.replace('/dev', '/main') + '/carts/sync' : ''
        ].filter(Boolean);
        const payload = JSON.stringify({
          user_id: u.id,
          items: items.map(i => ({ product_id: i.id, qty: i.qty }))
        });
        urls.forEach((url) => {
          try {
            if (navigator.sendBeacon) {
              const blob = new Blob([payload], { type: 'application/json' });
              navigator.sendBeacon(url, blob);
            } else {
              fetch(url, { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true, credentials: 'include' }).catch(() => { });
            }
          } catch (_e) { }
        });
      }
    } catch (_e) { }
    
    // Clear store state on logout
    try {
      sessionStorage.removeItem('store:state:v1');
    } catch (_e) { }
    
    setUser(null);
    localStorage.removeItem('auth:user');
    // Hit backend to clear cookie
    apiClient.post('/users/logout').catch(() => { });
  };

  const value = useMemo(() => ({
    isAuthenticated: Boolean(user),
    user,
    setUser,
    login,
    signup,
    logout,
    authChecked
  }), [user, authChecked]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}


